import { useState, useEffect } from "react";

/**
 * Hook personalizado para calcular el número de filas de una tabla basado en la altura de la ventana.
 * @param {Object} options - Opciones de configuración
 * @param {number} options.rowHeight - Altura estimada de una fila en px (default: 50)
 * @param {number} options.fixedOffset - Altura en px de elementos fijos (headers, etc) (default: 340)
 * @param {number} options.minRows - Mínimo número de filas a mostrar (default: 5)
 * @param {number} options.defaultRows - Valor inicial antes del cálculo (default: 10)
 * @returns {number} Número óptimo de filas
 */
const useDynamicRows = ({
  rowHeight = 50,
  fixedOffset = 340,
  minRows = 5,
  defaultRows = 10,
} = {}) => {
  const [rows, setRows] = useState(defaultRows);

  useEffect(() => {
    // Función de cálculo
    const calculateRows = () => {
      const windowHeight = window.innerHeight;
      const availableHeight = windowHeight - fixedOffset;

      // Calculamos cuántas filas caben
      let calculated = Math.floor(availableHeight / rowHeight);

      // Aplicamos límites
      calculated = Math.max(minRows, calculated);

      setRows(calculated);
    };

    // Calcular inmediatamente al montar
    calculateRows();

    // Debounce simple para el evento resize
    let timeoutId;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(calculateRows, 150);
    };

    window.addEventListener("resize", handleResize);

    // Cleanup
    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(timeoutId);
    };
  }, [rowHeight, fixedOffset, minRows]);

  return rows;
};

export default useDynamicRows;
